import './Navbar.css';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { IoLogOutOutline } from 'react-icons/io5';
import { HiOutlineChartBar } from 'react-icons/hi';
import { HiOutlineMap } from 'react-icons/hi';
import { HiOutlineAcademicCap } from 'react-icons/hi';
import { HiOutlineCheckCircle, HiOutlineInformationCircle, HiOutlineUsers, HiOutlineBookmarkSquare, HiOutlineArrowDownTray } from 'react-icons/hi2';
import { RiMenuUnfoldLine } from 'react-icons/ri';
import { useAuthStore } from '../../stores';
import { logout as logoutUser } from '../../services/authService';
import { isTauri } from '../../utils/tauri';
import DesktopDownloadModal from '../DesktopDownloadModal/DesktopDownloadModal';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const atlasLogo = require('../../assets/logos/atlas-lockup.png');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const atlasIcon = require('../../assets/logos/atlas-mark.png');

interface NavbarProps {
  onSidebarCollapse?: (collapsed: boolean) => void;
}

function Navbar({ onSidebarCollapse }: NavbarProps) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const isDesktop = isTauri();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);

  // Get user name from query parameter, fallback to Zustand store
  const queryUserName = searchParams.get('name');
  const userName = queryUserName || user?.name || 'Admin';
  const userRole = user?.role || 'Sales BDO';
  const userInitials = userName.slice(0, 2).toUpperCase();

  // Determine active tab from current pathname
  const getActiveTab = (): 'opportunities-by-retailer' | 'opportunities-by-region' | 'exported-opportunities' | 'your-lists' | 'user-list' => {
    if (location.pathname.includes('/opportunitybymap')) return 'opportunities-by-region';
    if (location.pathname.includes('/exported-opportunities')) return 'exported-opportunities';
    if (location.pathname.includes('/your-lists') || location.pathname.includes('/list-stores')) return 'your-lists';
    if (location.pathname.includes('/user-list')) return 'user-list';
    return 'opportunities-by-retailer';
  };

  const currentTab = getActiveTab();

  const handleNavItemClick = (route: string) => {
    navigate(route);
    // Close sidebar on mobile after selecting
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const handleLogoClick = () => {
    navigate('/dashboard');
  };

  const handleLogout = async () => {
    // Call the comprehensive logout from authService
    // This clears localStorage, sessionStorage, IndexedDB, and auth state
    await logoutUser();

    // Clear Zustand store (this will attempt to write to localStorage)
    logout();

    // Clear all storage again AFTER Zustand logout to prevent it from saving state
    localStorage.clear();
    sessionStorage.clear();

    // Navigate to login
    navigate('/');
  };

  const handleSidebarToggle = () => {
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
      setSidebarCollapsed(false); // Ensure sidebar is expanded when opened on mobile
    } else {
      const newCollapsedState = !sidebarCollapsed;
      setSidebarCollapsed(newCollapsedState);
      if (onSidebarCollapse) {
        onSidebarCollapse(newCollapsedState);
      }
    }
  };

  return (
    <>
      {/* Mobile Top Header */}
      {isMobile && (
        <div className="mobile-top-header">
          <button className="mobile-hamburger-btn" onClick={() => setSidebarOpen(true)} title="Open Menu">
            <RiMenuUnfoldLine />
          </button>
          <div className="mobile-top-logo" onClick={handleLogoClick}>
            <img src={atlasLogo} alt="Atlas Logo" className="mobile-top-logo-img" />
          </div>
        </div>
      )}

      {/* Sidebar Overlay (mobile) */}
      {isMobile && sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'} ${sidebarCollapsed ? 'collapsed' : ''}`}>
        {/* Sidebar Header */}
        <div className="sidebar-nav-header">
          <div className="sidebar-logo" onClick={handleLogoClick}>
            <img
              src={sidebarCollapsed ? atlasIcon : atlasLogo}
              alt="Atlas Logo"
              className="logo-image"
            />
          </div>
          
          
          <button className={`sidebar-toggle-btn ${!sidebarCollapsed ? 'rotated' : ''}`} onClick={handleSidebarToggle} title="Toggle Sidebar">
            <RiMenuUnfoldLine />
          </button>
          
        </div>

        {/* Dashboard Selector */}
        <div className="sidebar-dashboard-section">
          <div className="dashboard-label">Dashboard</div>
        </div>

        {/* Navigation Items */}
        <nav className="sidebar-nav">
          <button
            className={`sidebar-nav-item ${currentTab === 'opportunities-by-retailer' ? 'active' : ''}`}
            onClick={() => handleNavItemClick('/dashboard/opportunity')}
            title="Opportunities by Retailer"
          >
            {/* @ts-ignore */}
            <HiOutlineChartBar className="nav-item-icon" />
            <span className="nav-item-text">Opportunities by Retailer</span>
          </button>

          <button
            className={`sidebar-nav-item ${currentTab === 'opportunities-by-region' ? 'active' : ''}`}
            onClick={() => handleNavItemClick('/dashboard/opportunitybymap')}
            title="Opportunities by Region"
          >
            {/* @ts-ignore */}
            <HiOutlineMap className="nav-item-icon" />
            <span className="nav-item-text">Opportunities by Region</span>
          </button>

          <button
            className={`sidebar-nav-item ${currentTab === 'exported-opportunities' ? 'active' : ''}`}
            onClick={() => handleNavItemClick('/dashboard/exported-opportunities')}
            title="Exported Opportunities"
          >
            {/* @ts-ignore */}
            <HiOutlineCheckCircle className="nav-item-icon" />
            <span className="nav-item-text">Exported Opportunities</span>
          </button>

          <button
            className={`sidebar-nav-item ${currentTab === 'your-lists' ? 'active' : ''}`}
            onClick={() => handleNavItemClick('/dashboard/your-lists')}
            title="Your Lists"
          >
            {/* @ts-ignore */}
            <HiOutlineBookmarkSquare className="nav-item-icon" />
            <span className="nav-item-text">Your Lists</span>
          </button>

          <button
            className={`sidebar-nav-item ${currentTab === 'user-list' ? 'active' : ''}`}
            onClick={() => handleNavItemClick('/dashboard/user-list')}
            title="Members"
          >
            {/* @ts-ignore */}
            <HiOutlineUsers className="nav-item-icon" />
            <span className="nav-item-text">Members</span>
          </button>

          <a
            href="https://gatekeepersystemsinc-my.sharepoint.com/:f:/g/personal/jpande_gatekeepersystems_com/IgDLKv9C5YMaRbQiIHcd6GxnAUyM7V1HSrDlBqOJG7xuwaM?e=aPI2P8"
            target="_blank"
            rel="noopener noreferrer"
            className="sidebar-nav-item tutorial-link"
            title="Tutorial"
          >
            {/* @ts-ignore */}
            <HiOutlineAcademicCap className="nav-item-icon" />
            <span className="nav-item-text">Tutorial</span>
          </a>

        </nav>

        {/* Sidebar Footer */}
        <div className="sidebar-footer">
          {/* Desktop App Banner — web only */}
          {!isDesktop && (
            <button
              className="desktop-app-banner"
              onClick={() => setShowDownloadModal(true)}
              title="Get Atlas Desktop App"
            >
              {/* @ts-ignore */}
              <HiOutlineArrowDownTray className="desktop-app-banner-icon" />
              <div className="desktop-app-banner-text">
                <span className="desktop-app-banner-title">Get Desktop App</span>
                <span className="desktop-app-banner-sub">Windows · AI-powered</span>
              </div>
            </button>
          )}

          {/* Profile Section */}
          <div className="sidebar-profile-section">
            <div className="profile-avatar">{userInitials}</div>
            <div className="profile-info">
              <span className="profile-name">{userName}</span>
              <span className="profile-role">{userRole}</span>
            </div>
          </div>

          {/* Logout Button */}
          <button className="sidebar-footer-btn" onClick={handleLogout} title="Logout">
            {/* @ts-ignore */}
            <IoLogOutOutline className="footer-btn-icon" />
            <span>Logout</span>
          </button>
        </div>

        <div className="sidebar-version-info">
          <span className="version-date">Last updated: Jun 9, 2026</span>
          <div className="version-tooltip-wrapper">
            <HiOutlineInformationCircle className="version-info-icon" />
            <div className="version-tooltip">
              <div className="version-tooltip-title">What's New</div>
              <ul className="version-tooltip-list">
                <li>Rebranded application to Atlas</li>
                <li>Added store notes & comments</li>
                <li>Heatmap updates & improvements</li>
                <li>Usability changes for list & GK Account Penetration column</li>
                <li>New fields added to All Sites & filters</li>
                <li>Bug fixes & performance improvements</li>
              </ul>
            </div>
          </div>
        </div>
      </aside>

      {showDownloadModal && <DesktopDownloadModal onClose={() => setShowDownloadModal(false)} />}
    </>
  );
}

export default Navbar;
